import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  TrendingUp,
  DollarSign,
  PiggyBank,
  Wallet,
  BarChart3,
  FileText,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { useInvestorInvestmentOverview } from '@/hooks/react-query';
import { formatCurrency, formatPercentage } from '@/utils/formatters';
import type { InvestorInvestmentFundCommitment } from '@/types/investor';
import { InvestorTransfersCard } from './InvestorTransfersCard';

interface InvestorInvestmentOverviewProps {
  investorId: string;
}

export function InvestorInvestmentOverview({
  investorId,
}: InvestorInvestmentOverviewProps) {
  const navigate = useNavigate();
  const { data, isLoading, error } = useInvestorInvestmentOverview(investorId);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-sm text-muted-foreground">
            Loading investment overview...
          </p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Error loading investment overview</p>
          <p className="text-sm text-muted-foreground mt-2">{error.message}</p>
        </div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="flex items-center justify-center h-64">
        <p className="text-muted-foreground">No investment data available</p>
      </div>
    );
  }

  const {
    total_commitment: totalCommitment,
    drawn_capital: drawnCapital,
    undrawn_capital: undrawnCapital,
    total_invested: totalInvested,
    nav,
    total_fees: totalFees,
    total_unconverted_cla_exposure: totalClaExposure,
  } = data;

  const allUnconvertedClas = data.funds.flatMap(
    (f: InvestorInvestmentFundCommitment) =>
      (f.unconverted_clas || []).map((cla) => ({
        ...cla,
        fundName: f.name,
      }))
  );

  const totalReturn =
    drawnCapital > 0 ? ((nav - drawnCapital) / drawnCapital) * 100 : 0;
  const drawPercentage =
    totalCommitment > 0 ? (drawnCapital / totalCommitment) * 100 : 0;

  const formatFundCurrency = (value: number) =>
    formatCurrency(value, {
      currency: data.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

  // Component values are already in percentage (0-100); formatPercentage expects decimals (0-1)
  const formatPct = (value: number) => formatPercentage(value / 100, 1);

  const getStatusBadgeClass = (status: string) => {
    const statusLower = status.toLowerCase();
    if (statusLower === 'active')
      return 'bg-green-500/20 text-green-600 dark:text-green-400';
    if (statusLower === 'exited') return 'bg-muted text-muted-foreground';
    if (statusLower === 'pending')
      return 'bg-yellow-500/20 text-yellow-600 dark:text-yellow-400';
    return 'bg-muted text-muted-foreground';
  };

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Total Commitment
            </CardTitle>
            <DollarSign className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFundCurrency(totalCommitment)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              Across {data.fund_count} funds
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Drawn Capital
            </CardTitle>
            <Wallet className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFundCurrency(drawnCapital)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatPct(drawPercentage)} called
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Undrawn Capital
            </CardTitle>
            <PiggyBank className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFundCurrency(undrawnCapital)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {formatPct(100 - drawPercentage)} remaining
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Net Asset Value
            </CardTitle>
            <TrendingUp className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{formatFundCurrency(nav)}</div>
            <p
              className={`text-xs mt-1 ${totalReturn >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
            >
              {totalReturn >= 0 ? '↑' : '↓'} {formatPct(Math.abs(totalReturn))}{' '}
              return
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground">
              Unconverted CLA Exposure
            </CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">
              {formatFundCurrency(totalClaExposure)}
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              {nav > 0 ? formatPct((totalClaExposure / nav) * 100) : '0.0%'} of
              NAV
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Fund Commitments Table */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BarChart3 className="h-5 w-5" />
            Fund Commitments
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table
              className="min-w-full divide-y divide-border"
              data-testid="fund-commitments-table"
            >
              <thead className="bg-muted">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Fund Name
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Commitment
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Drawn
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Undrawn
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Invested
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    NAV
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Fees & Costs
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-medium text-muted-foreground uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-card divide-y divide-border">
                {data.funds.map((fund: InvestorInvestmentFundCommitment) => {
                  const fundReturn =
                    fund.drawn > 0
                      ? ((fund.nav - fund.drawn) / fund.drawn) * 100
                      : 0;

                  return (
                    <tr
                      key={fund.id}
                      className="hover:bg-accent cursor-pointer"
                      onClick={() => navigate(`/app/funds/${fund.id}`)}
                    >
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="text-sm font-medium text-foreground">
                          {fund.name}
                        </div>
                        <div className="text-xs text-muted-foreground">
                          {formatPct(fund.draw_percentage)} drawn
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-foreground">
                        {formatFundCurrency(fund.commitment)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-foreground">
                        {formatFundCurrency(fund.drawn)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-foreground">
                        {formatFundCurrency(fund.undrawn)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-foreground">
                        {formatFundCurrency(fund.invested)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right">
                        <div className="text-foreground">
                          {formatFundCurrency(fund.nav)}
                        </div>
                        <div
                          className={`text-xs ${fundReturn >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}
                        >
                          {fundReturn >= 0 ? '+' : ''}
                          {formatPct(fundReturn)}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-foreground">
                        {formatFundCurrency(fund.fees)}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-center">
                        <Badge className={getStatusBadgeClass(fund.status)}>
                          {fund.status.charAt(0).toUpperCase() +
                            fund.status.slice(1)}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot className="bg-muted">
                <tr className="font-semibold">
                  <td className="px-4 py-3 text-sm text-foreground">Total</td>
                  <td className="px-4 py-3 text-sm text-right text-foreground">
                    {formatFundCurrency(totalCommitment)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-foreground">
                    {formatFundCurrency(drawnCapital)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-foreground">
                    {formatFundCurrency(undrawnCapital)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-foreground">
                    {formatFundCurrency(totalInvested)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-foreground">
                    {formatFundCurrency(nav)}
                  </td>
                  <td className="px-4 py-3 text-sm text-right text-foreground">
                    {formatFundCurrency(totalFees)}
                  </td>
                  <td></td>
                </tr>
              </tfoot>
            </table>
          </div>
        </CardContent>
      </Card>

      <InvestorTransfersCard investorId={investorId} />

      {/* Active CLAs Table */}
      {allUnconvertedClas.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5" />
              Active Convertible Instruments
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-border">
                <thead className="bg-muted">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Company
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Fund
                    </th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Instrument Type
                    </th>
                    <th className="px-4 py-3 text-right text-xs font-medium text-muted-foreground uppercase tracking-wider">
                      Effective Exposure
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-card divide-y divide-border">
                  {allUnconvertedClas.map((cla) => (
                    <tr
                      key={`${cla.portfolio_company_name}-${cla.instrument_type}`}
                      className="hover:bg-accent"
                    >
                      <td className="px-4 py-4 whitespace-nowrap text-sm font-medium text-foreground">
                        {cla.portfolio_company_name}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground">
                        {cla.fundName}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-foreground capitalize">
                        {cla.instrument_type.replaceAll('_', ' ')}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-right text-foreground">
                        {formatFundCurrency(cla.effective_value)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
